import { systemFontFamily } from "common/BaseTheme";
import { panic } from "common/Support";

import { css, FlattenInterpolation, ThemeProps } from "styled-components";
import { disabledProps } from "webapp/styled-components";
import { styled } from "sharedUI/styles";

import {
    backgroundHoverStyles,
    fontColorMapping,
    backgroundColorMapping,
    ButtonType,
    ButtonVariants,
    ButtonStyleProps,
    ButtonStyles,
    InvalidButtonStyles,
    IconType,
    SplitType,
    ButtonSizes,
    ButtonSizeMap,
} from "./button-vnext-lib";
import { AnyIcon } from "sharedUI/ui/views/glide-icon/glide-icon";

const GradientFontColor = css`
    .label {
        background: ${p => p.theme.brandGradient};
        transition: text ${p => p.theme.transitionBase};
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
    }
    :hover {
        color: transparent;
    }
`;

const splitLeftStyle = css`
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
`;

const splitRightStyle = css`
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
`;

const iconRightMargin = css`
    .label {
        margin-right: 7px;
    }
`;

const iconLeftMargin = css`
    .label {
        margin-left: 7px;
    }
`;

const buttonSizes = {
    xsm: css`
        height: 24px;
        border-radius: 6px;
        font-size: 12px;
    `,
    sm: css`
        height: 28px;
        border-radius: 8px;
        font-size: 13px;
    `,
    md: css`
        height: 32px;
        border-radius: 9px;
        font-size: 13px;
    `,
    lg: css`
        height: 36px;
        border-radius: 9px;
        font-size: 14px;
    `,
    xlg: css`
        height: 40px;
        border-radius: 9px;
        font-size: 14px;
    `,
};

const buttonPadding = {
    xsm: css`
        padding: 0 8px;
    `,
    sm: css`
        padding: 0 10px;
    `,
    md: css`
        padding: 0 12px;
    `,
    lg: css`
        padding: 0 14px;
    `,
    xlg: css`
        padding: 0 16px;
    `,
};

const iconButtonPadding = {
    xsm: css`
        padding: 0 5px 0 3px;
    `,
    sm: css`
        padding: 0 5px 0 3px;
    `,
    md: css`
        padding: 0 7px 0 5px;
    `,
    lg: css`
        padding: 0 7px 0 5px;
    `,
    xlg: css`
        padding: 0 9px 0 7px;
    `,
};

const getButtonStyles = (type: ButtonType, variant: ButtonVariants): ButtonStyles => {
    const buttonStyle = `${type}${variant}` as ButtonStyles;
    if (InvalidButtonStyles.includes(buttonStyle)) {
        panic("Not a valid Button Style");
    }
    return buttonStyle;
};

const getHoverBGColor = (btnStyle: ButtonStyles) => {
    return backgroundHoverStyles[btnStyle];
};

const getButtonPadding = (size: ButtonSizes, iconType?: IconType, split?: SplitType, icon?: AnyIcon) => {
    if (split === "right" && icon && iconType === "iconOnly") {
        return iconButtonPadding[size];
    } else if (!split && icon && iconType === "iconOnly") {
        return css`
            padding: 0px;
        `;
    }
    return buttonPadding[size];
};

const getButtonWidth = (
    size: ButtonSizes,
    isFullWidth?: boolean,
    iconType?: IconType,
    split?: SplitType,
    icon?: AnyIcon
) => {
    if (!split && icon && iconType === "iconOnly") {
        return css`
            width: ${ButtonSizeMap[size]}px;
        `;
    } else if (isFullWidth) {
        return css`
            width: 100%;
        `;
    } else
        return css`
            width: auto;
        `;
};

const getMinWidth = (size: ButtonSizes, iconType?: IconType) => {
    if (iconType === "iconOnly") {
        return css`
            min-width: ${ButtonSizeMap[size]}px;
        `;
    }
    return css`
        min-width: ${ButtonSizeMap[size] * 2}px;
    `;
};

const getFontColor = (buttonStyle: ButtonStyles) => {
    let result: FlattenInterpolation<ThemeProps<any>> = css`
        color: ${p => p.theme.n0};
        :hover {
            color: ${p => p.theme.n0};
        }
    `;

    if (Object.keys(fontColorMapping).includes(buttonStyle)) {
        const val = fontColorMapping[buttonStyle];
        result = css`
            color: ${p => p.theme[val]};
            :hover {
                color: ${p => p.theme[val]};
            }
        `;
    } else if (buttonStyle.includes("primary") && buttonStyle !== "primaryinverse") {
        result = css`
            color: #ffffff;
            :hover {
                color: #ffffff;
            }
        `;
    } else if (buttonStyle.includes("gradient") && buttonStyle !== "primarygradient") {
        result = GradientFontColor;
    }

    return result;
};

const getHoverStyles = (type: ButtonType, variant: ButtonVariants) => {
    const buttonStyle = getButtonStyles(type, variant);
    let result: FlattenInterpolation<ThemeProps<any>> = css``;
    if (type === "primary") {
        result = css`
            &:hover,
            &:focus {
                .button-container:after {
                    background-color: rgba(255, 255, 255, 0.12);
                }
            }
        `;
    } else if (type === "secondary" || type === "minimal") {
        const bg = getHoverBGColor(buttonStyle);
        if (bg !== undefined)
            result = css`
                &:hover,
                &:focus {
                    background: ${p => p.theme[bg]};
                }
            `;
    } else if (type === "tertiary") {
        result = css`
            box-shadow: ${p => p.theme.shadow1};
            &:hover,
            &:focus {
                box-shadow: ${p => p.theme.shadow2};
            }
        `;
    }
    return result;
};

const getBackgroundColor = (buttonStyle: ButtonStyles) => {
    // Default to light grey if no background matches
    let result: FlattenInterpolation<ThemeProps<any>> = css`
        background-color: ${p => p.theme.accentPrimary};
    `;

    if (buttonStyle.includes("tertiary")) {
        result = css`
            background-color: ${p => p.theme.bgFront};
        `;
    } else if (buttonStyle.includes("minimal")) {
        result = css`
            background-color: transparent;
        `;
    } else if (Object.keys(backgroundColorMapping).includes(buttonStyle)) {
        const val = backgroundColorMapping[buttonStyle];
        result = css`
            background: ${p => p.theme[val]};
        `;
    }
    return result;
};

const iconTypes = {
    default: iconLeftMargin,
    iconLeading: iconLeftMargin,
    iconTrailing: css`
        flex-direction: row-reverse;
        ${iconRightMargin}
    `,
    segmented: css`
        flex-direction: row-reverse;
        ${iconRightMargin}
    `,
    iconOnly: css`
        flex-direction: row;
        .label {
            display: none;
        }
    `,
};

export const Button = styled.button<ButtonStyleProps>`
    font-family: ${systemFontFamily};
    font-weight: 600;
    text-align: center;
    transition: all ${p => p.theme.transitionSlow};
    cursor: pointer;

    ${p => getButtonWidth(p.size, p.isFullWidth, p.iconType, p.split, p.icon)};

    ${p => buttonSizes[p.size]};
    ${p => getBackgroundColor(getButtonStyles(p.buttonType, p.variant))}
    ${p => getFontColor(getButtonStyles(p.buttonType, p.variant))};
    ${p => getHoverStyles(p.buttonType, p.variant)}
    ${p => getMinWidth(p.size, p.iconType)};

    ${p => p.disabled && disabledProps}

    &.middle,
    &.left {
        ${splitLeftStyle}
        .button-container {
            ${splitLeftStyle}
        }
    }

    &.middle,
    &.right {
        border-left: 1px solid ${p => (p.buttonType === "primary" ? "rgba(255, 255, 255, 0.14)" : "n200a")};

        ${splitRightStyle}
        .button-container {
            ${splitRightStyle}
        }
    }

    .hide {
        visibility: hidden;
        opacity: 0;
    }

    &:hover {
        transition: all ${p => p.theme.transitionBase};
    }

    .label {
        display: block;
        text-align: center;
        margin: auto 0;
        width: 100%;
        transition: all ${p => p.theme.transitionBase};
    }

    .named-icon {
        transition: all ${p => p.theme.transitionBase};
    }

    .process-icon {
        display: flex;
        position: absolute;
        width: 100%;
        left: 0px;
        transition: all ${p => p.theme.transitionBase};
        color: ${p => (p.variant === "gradient" && p.buttonType !== "primary" ? p.theme.b400 : "currentColor")};
    }

    .button-container {
        ${p => getButtonPadding(p.size, p.iconType, p.split, p.icon)};
        position: relative;
        display: flex;
        align-items: center;
        justify-content: ${p => (p.iconType === "iconOnly" ? "center" : "space-between")};
        transition: all ${p => p.theme.transitionBase};
        border-radius: inherit;
        ${p => p.iconType !== undefined && p.icon && iconTypes[p.iconType]};
        width: 100%;
        height: 100%;

        /* Only used for primary buttons */
        &:after {
            content: "";
            position: absolute;
            inset: 0;
            background: rgba(255, 255, 255, 0);
            transition: all ${p => p.theme.transitionBase};
            border-radius: inherit;
        }
    }
`;
